import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const LoginPage = () => {
  const { login, verify2FA } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAEmail, setTwoFAEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.requires_2fa) {
        setRequires2FA(true);
        setTwoFAEmail(result.email);
      } else if (result.success) {
        // Redirect based on role
        const userRole = result.user.role;
        if (userRole === 'super_admin') {
          navigate('/sad/dashboard');
        } else if (userRole === 'shop_admin' || userRole === 'manager' || userRole === 'cashier') {
          navigate('/shop/dashboard');
        } else if (userRole === 'invoice_user') {
          navigate('/invoices/dashboard');
        } else {
          navigate(redirect);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verify2FA(twoFAEmail, twoFACode);
      if (result.success) {
        const userRole = result.user.role;
        if (userRole === 'super_admin') {
          navigate('/sad/dashboard');
        } else if (userRole === 'shop_admin' || userRole === 'manager' || userRole === 'cashier') {
          navigate('/shop/dashboard');
        } else if (userRole === 'invoice_user') {
          navigate('/invoices/dashboard');
        } else {
          navigate(redirect);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1e3a5f] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center">
              <span className="text-white font-bold text-xl">V</span>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">
              VendFactura
            </span>
          </Link>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            {language === 'es' 
              ? 'Gestiona tu negocio con confianza'
              : 'Manage your business with confidence'}
          </h1>
          <p className="text-white/70 text-lg">
            {language === 'es'
              ? 'TPV y facturación VeriFactu para tiendas españolas'
              : 'POS and VeriFactu invoicing for Spanish shops'}
          </p>
        </div>
        
        <div className="text-white/50 text-sm">
          © 2025 VendFactura
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#1e3a5f] rounded-sm flex items-center justify-center">
                <span className="text-white font-bold text-xl">V</span>
              </div>
              <span className="text-xl font-bold text-[#1e3a5f] tracking-tight">
                VendFactura
              </span>
            </Link>
          </div>

          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>

          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-2">
            {language === 'es' ? 'Iniciar Sesión' : 'Log In'}
          </h2>
          <p className="text-slate-600 mb-8">
            {language === 'es' 
              ? '¿No tienes cuenta?' 
              : "Don't have an account?"}
            {' '}
            <Link to="/register" className="text-[#22c55e] font-medium hover:underline">
              {language === 'es' ? 'Regístrate' : 'Sign up'}
            </Link>
          </p>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </motion.div>
          )}

          {!requires2FA ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="vf-input w-full pl-12 pr-4 py-3"
                    placeholder="tu@email.com"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                  {language === 'es' ? 'Contraseña' : 'Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="vf-input w-full pl-12 pr-12 py-3"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 border-2 border-slate-300" />
                  <span className="text-sm text-slate-600">
                    {language === 'es' ? 'Recordarme' : 'Remember me'}
                  </span>
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#22c55e] hover:underline"
                >
                  {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot password?'}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  language === 'es' ? 'Iniciar Sesión' : 'Log In'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div className="text-center mb-6">
                <p className="text-slate-600">
                  {language === 'es' 
                    ? 'Hemos enviado un código de verificación a tu email'
                    : 'We sent a verification code to your email'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                  {language === 'es' ? 'Código de Verificación' : 'Verification Code'}
                </label>
                <input
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  required
                  maxLength={6}
                  className="vf-input w-full px-4 py-3 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  data-testid="2fa-code-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="2fa-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  language === 'es' ? 'Verificar' : 'Verify'
                )}
              </button>

              <button
                type="button"
                onClick={() => setRequires2FA(false)}
                className="w-full text-center text-slate-600 hover:text-[#1e3a5f]"
              >
                {language === 'es' ? 'Volver al login' : 'Back to login'}
              </button>
            </form>
          )}

          {/* Staff PIN Login Link */}
          <div className="mt-8 pt-8 border-t border-slate-200 text-center">
            <Link 
              to="/shop/staff-login"
              className="text-slate-600 hover:text-[#1e3a5f]"
            >
              {language === 'es' ? '¿Eres empleado? Accede con PIN' : 'Staff member? Login with PIN'}
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
